import {NodeType} from "crqlar-react-common/dist/models/automation/automation";
import {triggerNodeTypes} from "../config/nodeCategories";
import {NODE_FOOTPRINT} from "./nodePlacement";

/** One column: a 220 px card and the 60 px between two of them. */
export const LAYOUT_LANE_SPACING = 280;

/** A step's default offset below another (appending, the agent's drafts). */
export const LAYOUT_ROW_SPACING = 170;

const LAYOUT_ROW_GAP = 72;

export interface LayoutNodeLike {
    id: string;
    data?: {type?: NodeType};
    /** The card's measured height; a card not drawn yet counts as [NODE_FOOTPRINT]'s. */
    height?: number | null;
}

export interface LayoutEdgeLike {
    source: string;
    target: string;
    sourceHandle?: string | null;
}

interface LayoutPosition {
    x: number;
    y: number;
}

// Columns go yes, then plain, then no.
const handleRank = (handle?: string | null): number => handle === 'true' ? 0 : handle === 'false' ? 2 : 1;

const isTriggerNode = (node: LayoutNodeLike) => (
    !!node.data?.type && triggerNodeTypes.includes(node.data.type)
);

/**
 * Must match the API's template layout (`TemplateLayout.kt`). Down: each card hangs LAYOUT_ROW_GAP below the
 * lowest card leading to it. Across: a tidy tree; an If keeps yes left and no right, even with one side open.
 */
export const computeAutoLayout = (
    nodes: LayoutNodeLike[],
    edges: LayoutEdgeLike[]
): Record<string, LayoutPosition> => {
    const positions: Record<string, LayoutPosition> = {};
    if (!nodes.length) return positions;

    const byId = new Map(nodes.map(node => [node.id, node]));
    const known = edges.filter(edge => byId.has(edge.source) && byId.has(edge.target));
    const outgoing = new Map<string, LayoutEdgeLike[]>();
    known.forEach(edge => outgoing.set(edge.source, [...(outgoing.get(edge.source) ?? []), edge]));
    outgoing.forEach(list => list.sort((a, b) => handleRank(a.sourceHandle) - handleRank(b.sourceHandle)));
    const hasIncoming = new Set(known.map(edge => edge.target));

    const roots = [
        ...nodes.filter(isTriggerNode),
        ...nodes.filter(node => !hasIncoming.has(node.id)),
        ...nodes
    ];

    // The tree the columns follow: each step under the first step that reaches it.
    const treeChildren = new Map<string, (string | null)[]>();
    const treeParent = new Map<string, string>();
    const claimed = new Set<string>();
    const acyclic: LayoutEdgeLike[] = [];

    const grow = (id: string, onPath: Set<string>) => {
        onPath.add(id);
        const children: (string | null)[] = [];
        const handles: (string | null | undefined)[] = [];
        treeChildren.set(id, children);
        (outgoing.get(id) ?? []).forEach(edge => {
            if (onPath.has(edge.target)) return; // a loop back up is drawn, not laid out
            acyclic.push(edge);
            if (claimed.has(edge.target)) return;
            claimed.add(edge.target);
            treeParent.set(edge.target, id);
            children.push(edge.target);
            handles.push(edge.sourceHandle);
            grow(edge.target, onPath);
        });
        // An If with one side open keeps the other side's column empty.
        if (byId.get(id)?.data?.type === NodeType.IF_CONDITION && children.length === 1) {
            if (handles[0] === 'false') children.unshift(null);
            else children.push(null);
        }
        onPath.delete(id);
    };

    const starts: string[] = [];
    roots.forEach(node => {
        if (claimed.has(node.id)) return;
        claimed.add(node.id);
        starts.push(node.id);
        grow(node.id, new Set());
    });

    const heightOf = (id: string) => byId.get(id)?.height || NODE_FOOTPRINT.height;
    const parents = new Map<string, string[]>();
    acyclic.forEach(edge => parents.set(edge.target, [...(parents.get(edge.target) ?? []), edge.source]));
    const top = new Map<string, number>();
    const topOf = (id: string): number => {
        const cached = top.get(id);
        if (cached !== undefined) return cached;
        const value = Math.max(0, ...(parents.get(id) ?? []).map(parent => topOf(parent) + heightOf(parent) + LAYOUT_ROW_GAP));
        top.set(id, value);
        return value;
    };
    nodes.forEach(node => topOf(node.id));

    // A step nothing leads to hangs right above the highest step it leads to.
    nodes.filter(node => !parents.get(node.id)?.length).forEach(root => {
        const below = acyclic.filter(edge => edge.source === root.id).map(edge => top.get(edge.target)!);
        if (below.length) top.set(root.id, Math.min(...below) - LAYOUT_ROW_GAP - heightOf(root.id));
    });

    const column = new Map<string, number>();
    let nextColumn = 0;
    const place = (id: string): number => {
        const children = treeChildren.get(id) ?? [];
        let x: number;
        if (!children.length) {
            x = nextColumn++;
        } else {
            const xs = children.map(child => child === null ? nextColumn++ : place(child));
            x = (xs[0] + xs[xs.length - 1]) / 2;
        }
        column.set(id, x);
        return x;
    };

    // A trigger whose steps are all under another one has no tree of its own.
    const feeders = starts.filter(id => !treeChildren.get(id)?.length && (outgoing.get(id) ?? []).length > 0);
    starts.filter(id => !feeders.includes(id)).forEach((start, index) => {
        if (index > 0) nextColumn++; // one empty column between flows that do not touch
        place(start);
    });

    const feedersByTarget = new Map<string, string[]>();
    feeders.forEach(id => {
        const target = outgoing.get(id)![0].target;
        feedersByTarget.set(target, [...(feedersByTarget.get(target) ?? []), id]);
    });
    feedersByTarget.forEach((group, target) => {
        const parent = treeParent.get(target);
        const parentChildren = parent ? treeChildren.get(parent) ?? [] : [];
        if (parent && starts.includes(parent) && parentChildren.length === 1 && parentChildren[0] === target) {
            const row = [parent, ...group];
            row.forEach((id, index) => column.set(id, column.get(target)! + index - (row.length - 1) / 2));
        } else {
            group.forEach(id => column.set(id, nextColumn++));
        }
    });

    const left = Math.min(...Array.from(column.values()));
    const highest = Math.min(...Array.from(top.values()));
    nodes.forEach(node => {
        positions[node.id] = {
            x: (column.get(node.id)! - left) * LAYOUT_LANE_SPACING,
            y: top.get(node.id)! - highest
        };
    });
    return positions;
};
